import { useEffect, useRef, useState } from "react";
import { Loader2, Search, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import ReviewFindings from "./ReviewFindings";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;

/** PRC-REV — THE GATE, in front of Issue and the first vendor e-mail.
 *  < threshold  → "Perform Procurement Review?" Yes / Skip (skip logged)
 *  ≥ threshold  → runs automatically, no option
 *  high findings → acknowledge before continuing; unavailable ≥ threshold → acknowledge.
 *  Never a hard block: `Continue` is enabled the moment the gate says can_proceed. */
export default function ReviewGate({ project, po, action = "issue", onProceed, onClose }) {
  const [gate, setGate] = useState(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const autoRan = useRef(false);

  const run = async (mode) => {
    setRunning(true);
    try {
      const res = await opsApi.poReviewRun(project.id, po.id, { mode });
      setGate(res.gate);
    } catch (e) { toast.error(e?.response?.data?.detail || "The review could not run"); }
    finally { setRunning(false); }
  };

  useEffect(() => {
    opsApi.poReview(project.id, po.id).then(setGate)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not read the review gate"));
  }, [project.id, po.id]);

  useEffect(() => {
    if (!gate || autoRan.current) return;
    if (gate.needs === "run") { autoRan.current = true; run("auto"); }
    else if (action === "review" && !gate.review_current) { autoRan.current = true; run("manual"); }
  }, [gate]); // eslint-disable-line react-hooks/exhaustive-deps

  const skip = async () => {
    setBusy(true);
    try {
      const g = await opsApi.poReviewSkip(project.id, po.id);
      toast.message(`Review skipped on ${po.po_number} — logged`);
      setGate(g);
      if (g.can_proceed) onProceed();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be skipped"); }
    finally { setBusy(false); }
  };

  const verb = { email: "Continue to the vendor e-mail", review: "Done" }[action] || "Continue to issue";
  const manual = action === "review";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="prc-gate">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <Search className="h-4 w-4 text-[#007BFF]" /> Procurement review · {po.po_number}
        </h3>
        {gate && (
          <p className="mt-1 text-xs text-slate-500" data-testid="prc-gate-mode">
            {money(gate.total)} — {gate.mode === "auto"
              ? `at or above the ${money(gate.threshold)} threshold, so the review runs automatically`
              : `below the ${money(gate.threshold)} threshold, so the review is optional`}
          </p>
        )}

        {!gate && (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking the gate…
          </div>
        )}

        {gate && running && (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-600" data-testid="prc-running">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking the PO against the submittal, the FRS,
            the quote, the specs and the change orders…
          </div>
        )}

        {gate && !running && gate.needs === "prompt" && !manual && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="prc-prompt">
            <p className="text-sm font-medium text-slate-900">Perform Procurement Review?</p>
            <p className="mt-1 text-xs text-slate-600">
              One check of this PO against the approved submittal, the Current FRS, the vendor quote and the
              processed change orders. Skipping is logged with your name.
              {!gate.available && <> <b>The review is unavailable right now</b> — {gate.unavailable_reason}.</>}
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => run("prompt")} disabled={busy} data-testid="prc-prompt-yes">
                Yes, review it
              </Button>
              <Button size="sm" variant="outline" onClick={skip} disabled={busy} data-testid="prc-prompt-skip">
                Skip (logged)
              </Button>
            </div>
          </div>
        )}

        {gate && !running && gate.review && (gate.needs !== "prompt" || manual) && (
          <div className="mt-3">
            {gate.needs === "acknowledge" && (
              <p className="mb-2 flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
                 data-testid="prc-needs-ack">
                <ShieldAlert className="h-3.5 w-3.5" /> {gate.reason}. Acknowledge each high finding to continue —
                the review never holds a PO; you do.
              </p>
            )}
            {gate.needs === "acknowledge_unavailable" && (
              <p className="mb-2 flex items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-xs text-slate-800"
                 data-testid="prc-needs-ack-unavailable">
                <ShieldAlert className="h-3.5 w-3.5" /> {gate.reason}.
              </p>
            )}
            <ReviewFindings gate={gate} project={project} poId={po.id} canAck onChanged={setGate} />
          </div>
        )}

        {gate && !running && gate.skipped && !gate.review && (
          <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="prc-skipped">
            The review was skipped on this version of the PO (logged).
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          {gate?.review && gate.review_current && (
            <Button size="sm" variant="ghost" onClick={() => run("manual")} disabled={busy || running}
                    data-testid="prc-rerun" className="mr-auto text-slate-500">Re-run</Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="prc-gate-cancel">Cancel</Button>
          <Button size="sm" onClick={onProceed} disabled={(!manual && !gate?.can_proceed) || running || busy}
                  data-testid="prc-gate-continue">{verb}</Button>
        </div>
      </div>
    </div>
  );
}
